export type CoachCTAType = 'insight' | 'action' | 'schedule' | 'plan_confirm' | 'mom' | 'feedback';

export interface CoachCTA {
  type: CoachCTAType;
  fields: Record<string, string>;
}

function normalizeKey(k: string) {
  return k.trim().toLowerCase().replace(/\s+/g, '_');
}

function normalizeTimeframe(tf?: string): string | undefined {
  if (!tf) return undefined;
  const v = tf.trim().toLowerCase().replace(/_/g, ' ');
  if (v.includes('today')) return 'today';
  if (v.includes('next week')) return 'next week';
  if (v.includes('in 2 weeks') || v.includes('two weeks')) return 'in 2 weeks';
  if (v.includes('this week')) return 'this week';
  return tf;
}

export function parseCoachCTABlocks(text: string): CoachCTA[] {
  if (!text || typeof text !== 'string') return [];
  const blocks: CoachCTA[] = [];
  const regex = /\[CTA:([a-zA-Z_]+)\][\r\n]+([\s\S]*?)(?=(?:^\[CTA:)|\Z)/gim;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const typeRaw = match[1] || '';
    const body = match[2] || '';
    const type = typeRaw.trim().toLowerCase() as CoachCTAType;
    const fields: Record<string, string> = {};

    body
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .forEach((line) => {
        const kv = line.split('=');
        if (kv.length >= 2) {
          const key = normalizeKey(kv.shift() as string);
          const value = kv.join('=');
          fields[key] = value.trim();
        }
      });

    if (type === 'action') {
      if (!fields['label']) continue;
      if (fields['timeframe']) fields['timeframe'] = normalizeTimeframe(fields['timeframe']) || fields['timeframe'];
      if (fields['priority']) fields['priority'] = fields['priority'].toLowerCase();
    }

    if (type === 'insight') {
      if (!fields['text'] && !fields['label']) continue;
      if (!fields['text'] && fields['label']) fields['text'] = fields['label'];
    }

    if (type === 'schedule') {
      if (fields['timeframe']) fields['timeframe'] = normalizeTimeframe(fields['timeframe']) || fields['timeframe'];
    }

    blocks.push({ type, fields });
  }
  return blocks;
}

/**
 * Strictly extract only the first valid CTA block found in the text.
 * Used to prevent UI clutter if the AI accidentally generates multiple blocks.
 */
export function extractFirstCTABlock(text: string): CoachCTA | null {
  const allBlocks = parseCoachCTABlocks(text);
  return allBlocks.length > 0 ? allBlocks[0] : null;
}
